function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const SLAVE_CONFIGURATION_FIELDS = [
  'address',
  'configured_only',
  'zone',
  'abilities',
  'capabilities',
  'dev_variant',
  'unique_id',
  'baud',
  'firmware_string'
];
const MULTI_OUT_INFO_FIELDS = ['slave', 'state', 'info', 'type', '_index'];
const MULTI_OUT_SETTINGS_FIELDS = [
  'id',
  'func',
  'type_id',
  'zone',
  'func_index',
  'overwr_select',
  'overwr_val_ana',
  'overwr_add_sett',
  'overwr_timeout',
  'memo'
];

function reconcileOwnedFields(current, next, fields) {
  for (const field of fields) {
    if (field in next) {
      const reconciled = reconcileState(current[field], next[field]);
      if (reconciled !== current[field]) current[field] = reconciled;
    } else {
      delete current[field];
    }
  }
}

function findMultiOut(current, next, fallbackIndex) {
  if ('id' in next) {
    return current.find((entry) => entry?.id === next.id);
  }
  if ('_index' in next) {
    return current.find((entry) => entry?._index === next._index);
  }
  return current[fallbackIndex];
}

function reconcileMultiOuts(current, next) {
  if (!Array.isArray(current)) return next;

  const reconciled = next.map((nextEntry, index) => {
    const currentEntry = findMultiOut(current, nextEntry, index);
    if (!isRecord(currentEntry) || !isRecord(nextEntry)) return nextEntry;

    reconcileOwnedFields(currentEntry, nextEntry, MULTI_OUT_INFO_FIELDS);

    // The server omits every settings-owned field from its once-per-second
    // GCSPIInfoIO updates. Presence of any such field means the complete
    // GCSPIConfigMultiIO entry accompanied this info entry.
    // V2 live info includes the physical ID without owning editable settings.
    if ('id' in nextEntry) currentEntry.id = nextEntry.id;
    if (MULTI_OUT_SETTINGS_FIELDS.some((field) => field !== 'id' && field in nextEntry)) {
      reconcileOwnedFields(currentEntry, nextEntry, MULTI_OUT_SETTINGS_FIELDS);
    }

    return currentEntry;
  });

  current.splice(0, current.length, ...reconciled);
  return current;
}

export function reconcileSlavesState(current, next) {
  if (!Array.isArray(current) || !Array.isArray(next)) return next;

  const reconciled = next.map((nextSlave, index) => {
    const currentSlave =
      isRecord(nextSlave) && 'address' in nextSlave
        ? current.find((slave) => slave?.address === nextSlave.address)
        : current[index];
    if (!isRecord(currentSlave) || !isRecord(nextSlave)) return nextSlave;

    reconcileOwnedFields(currentSlave, nextSlave, SLAVE_CONFIGURATION_FIELDS);
    if ('status' in nextSlave) currentSlave.status = nextSlave.status;
    if ('multi_outs' in nextSlave) {
      currentSlave.multi_outs = reconcileMultiOuts(
        currentSlave.multi_outs,
        nextSlave.multi_outs
      );
    }
    return currentSlave;
  });

  current.splice(0, current.length, ...reconciled);
  return current;
}

export function reconcilePresentCategories(current, next) {
  const target = isRecord(current) ? current : {};
  for (const [category, value] of Object.entries(next)) {
    const reconciled = reconcileState(target[category], value);
    if (reconciled !== target[category]) target[category] = reconciled;
  }
  return target;
}

export function reconcileStatePreservingFields(current, next, preservedFields) {
  if (!isRecord(current) || !isRecord(next)) return next;

  for (const key of Object.keys(current)) {
    if (!(key in next) && !preservedFields.has(key)) delete current[key];
  }
  for (const [key, value] of Object.entries(next)) {
    const reconciled = reconcileState(current[key], value);
    if (reconciled !== current[key]) current[key] = reconciled;
  }
  return current;
}

export function reconcileState(current, next) {
  if (Array.isArray(next)) {
    if (!Array.isArray(current)) return next;

    current.length = next.length;
    for (let index = 0; index < next.length; index += 1) {
      const reconciled = reconcileState(current[index], next[index]);
      if (reconciled !== current[index]) current[index] = reconciled;
    }
    return current;
  }

  if (isRecord(next)) {
    if (!isRecord(current)) return next;

    for (const key of Object.keys(current)) {
      if (!(key in next)) delete current[key];
    }
    for (const [key, value] of Object.entries(next)) {
      const reconciled = reconcileState(current[key], value);
      if (reconciled !== current[key]) current[key] = reconciled;
    }
    return current;
  }

  return Object.is(current, next) ? current : next;
}
